import { JWT } from 'aws-amplify/auth';
import axios from 'axios';
import { IDocument } from 'src/types/documents.types';
import { ICreateUserActivity } from 'src/types/userActivity.types';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
// const SERVER_URL = 'https://apolloqa.dev.idgmc.org/api';

const API_VERSION = 'v1';

const ROUTES = {
  HOSPITALS: 'hospitals',
  AUTH_PATIENTS: 'authPatients',
  USER_ACTIVITY_LOG: 'userActivityLog',
  DOCUMENTS: 'documents',
  PRIVATE_USER: 'privateUser'
};

export const END_POINTS = {
  PRE_AUTH: `${SERVER_URL}/${API_VERSION}/${ROUTES.AUTH_PATIENTS}/preAuth`,
  SEND_EMAIL_TO_SUPPORT: `${SERVER_URL}/${API_VERSION}/emailToSupport`,
  PATIENT_DETAILS: `${SERVER_URL}/${API_VERSION}/${ROUTES.AUTH_PATIENTS}/patientDetails`,
  DOCUMENTS_LIST: `${SERVER_URL}/${API_VERSION}/${ROUTES.DOCUMENTS}/getDocumentsList`,
  DOCUMENT: `${SERVER_URL}/${API_VERSION}/${ROUTES.DOCUMENTS}/getDocument`,
  USER_ACTIVITY_LOG: `${SERVER_URL}/${API_VERSION}/${ROUTES.USER_ACTIVITY_LOG}`,
  HOSPITALS: `${SERVER_URL}/${API_VERSION}/${ROUTES.HOSPITALS}`,
  DOCUMENTS: `${SERVER_URL}/${API_VERSION}/${ROUTES.DOCUMENTS}`,
  PRIVATE_USER: `${SERVER_URL}/${API_VERSION}/${ROUTES.PRIVATE_USER}`
};

class ApiService {
  private static _instance = ApiService;
  private _authorization: null | string = null;

  public static get instance() {
    this._instance = this._instance || new this();
    return this._instance;
  }

  public setAuthorization(token: JWT | string) {
    this._authorization = `Bearer ${token}`;
  }
  public removeAuthorization() {
    this._authorization = null;
  }

  private _getHeaders = () => {
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    };
    if (this._authorization) {
      headers.Authorization = this._authorization;
    }
    return headers;
  };

  public async sendEmailToSupport(data: {
    userName: string;
    subject: string;
    content: string;
    phoneNumber: string;
    hospitalCode: string;
  }) {
    return axios.request<{ isSend: string }>({
      url: END_POINTS.SEND_EMAIL_TO_SUPPORT,
      method: 'post',
      headers: this._getHeaders(),
      data
    });
  }

  public async preAuth(data: {
    id: string;
    phone_number: string;
    hospitalCode: string;
  }) {
    return axios.request<{ isMatch: string }>({
      url: END_POINTS.PRE_AUTH,
      method: 'post',
      headers: this._getHeaders(),
      data
    });
  }

  public async getPatientDetailes(data: {
    HospitalCode?: string;
    DeviceId?: string;
    Token?: string;
    IsPatient?: boolean;
  }) {
    return axios.request({
      url: END_POINTS.PATIENT_DETAILS,
      method: 'post',
      headers: this._getHeaders(),
      data
    });
  }

  /*
|-----------------------------------------------------------------
| HOSPITALS
|-----------------------------------------------------------------
*/
  public async fetchHospitals(query?: any) {
    return axios.request({
      url: `${END_POINTS.PRIVATE_USER}/private_HospitalsBeforeLogin`,
      method: 'get',
      headers: this._getHeaders(),
      params: query
    });
  }

  public async fetchHospital(query?: { hospitalCode: string }) {
    return axios.request({
      url: `${END_POINTS.HOSPITALS}/getHospital`,
      method: 'get',
      headers: this._getHeaders(),
      params: query
    });
  }

  /*
 |-----------------------------------------------------------------
 | DOCUMENTS
 |-----------------------------------------------------------------
 */
  public async fetchDocumentsList(query?: {
    isPatient: boolean;
    unitCode: string;
  }) {
    if (!this._authorization) {
      console.log('no _authorization');
      throw Error('Missing authorization token');
    }

    const res = await axios.request({
      url: `${END_POINTS.DOCUMENTS_LIST}`,
      method: 'post',
      headers: this._getHeaders(),
      data: query
    });

    return res;
  }

  /*
 |-----------------------------------------------------------------
 | DOCUMENT
 |-----------------------------------------------------------------
 */
  public async fetchDocument(query?: {
    docType: string;
    docNum: string;
    docVersion: string;
    docPart: string;
  }) {
    if (query == undefined) {
      return undefined;
    }

    if (!this._authorization) {
      console.log('no _authorization');
      throw Error('Missing authorization token');
    }

    const res = await axios.request({
      url: `${END_POINTS.DOCUMENT}`,
      method: 'post',
      headers: this._getHeaders(),
      data: {
        docType: query.docType,
        docNum: query.docNum,
        docVersion: query.docVersion,
        docPart: query.docPart,
        isPatient: true
      }
    });

    return res.data;
  }

  public async markDocumentAsRead(item: IDocument) {
    return axios.request({
      url: `${END_POINTS.DOCUMENTS}/markAsRead`,
      method: 'post',
      headers: this._getHeaders(),
      data: {
        docNum: item.docNum,
        caseId: item.caseId
      }
    });
  }

  /*
|-----------------------------------------------------------------
| USER ACTIVITY LOG
|-----------------------------------------------------------------
*/
  public async createActivityLog(data: ICreateUserActivity) {
    return axios.request({
      url: END_POINTS.USER_ACTIVITY_LOG,
      method: 'post',
      headers: this._getHeaders(),
      data
    });
  }
}
export default new ApiService();
